import { router } from 'expo-router';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
  ActionSheet,
  Button,
  ErrorCard,
  IconErrorFill,
  IconLocationFill,
  Skeleton,
  SkeletonTextBlock,
  Text,
  tokens,
} from '@fhs/ui';
import { usePathnameRef } from '@fhs/utils';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { useInRestaurantRedemptionContext } from '@fhs-legacy/frontend/src/state/loyalty/in-restaurant-redemption';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';

import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';
import { useMaxRedemptionLimitAlert } from '../../hooks/use-max-redemption-limit-alert';
import { useLoyaltyIncentives } from '../../queries/loyalty.queries';

import { DisplayCard, DisplayCardProps } from './display-card';
import { CardContent } from './reward-tier-card-content';

function renderItem({ item }: { item: DisplayCardProps }) {
  return <DisplayCard {...item} />;
}

function renderSeparator() {
  return <View style={styles.separator} />;
}

function RewardTierSkeleton() {
  return (
    <DisplayCard
      style={styles.skeletonCard}
      image={<Skeleton style={styles.imageSkeleton} />}
      button={<Skeleton style={styles.buttonSkeleton} />}
    >
      <SkeletonTextBlock lines={2} lineStyle={{ marginBottom: 4 }} style={styles.skeletonText} />
    </DisplayCard>
  );
}

function RedemptionUnavailable() {
  const handleViewRewards = useCallback(() => {
    router.dismiss();
    setTimeout(() => {
      router.navigate(routes.rewards);
    }, 100);
  }, []);

  return (
    <View style={styles.unavailableContainer}>
      <View style={styles.unavailableContent}>
        <IconErrorFill size={24} color={tokens.colors.$blackOpacity10} />
        <Text.Heading type="two" style={styles.unavailableTitle}>
          In-Restaurant Redemption is Currently Unavailable
        </Text.Heading>
        <Text.Ui size="md" style={styles.unavailableSubtitle}>
          Please order online to redeem your rewards.
        </Text.Ui>
      </View>
      <Button size="md" onPress={handleViewRewards}>
        <Button.Text>View Rewards</Button.Text>
      </Button>
    </View>
  );
}

export function RedeemTab() {
  const isRedeemTabEnabled = useFlag('enable-in-store-rewards-redeem');
  const { data, isError, refetch, isLoading: isLoadingIncentives } = useLoyaltyIncentives();
  const { loyaltyUserReady, noStoreSelected } = useLoyaltyLegacyStates();
  const { getCurrentPathname } = usePathnameRef();
  const isLoading = isLoadingIncentives || !loyaltyUserReady;
  const intl = useIntl();
  const { bottom } = useSafeAreaInsets();

  const hasReachedMaxRedemptionLimit = useAppSelector(
    selectors.loyalty.createHasReachedMaxRedemptionLimitSelector(intl)
  );

  const renderMaxRedemptionLimitAlert = useMaxRedemptionLimitAlert();

  const { setInStoreRewardTierId } = useInRestaurantRedemptionContext();
  const onPressRedeem = useCallback(
    (_rewardTierId: string) => {
      setInStoreRewardTierId(_rewardTierId);
    },
    [setInStoreRewardTierId]
  );

  const cardsToRender = useMemo(() => {
    return (
      data?.rewardTiers?.reduce<DisplayCardProps[]>((cards, rewardTier) => {
        if (rewardTier.isRedeemable) {
          const isRedeemable = rewardTier.isRedeemable && !hasReachedMaxRedemptionLimit;
          cards.push({
            image: rewardTier.image,
            button: {
              label: 'Redeem',
              onPress: () => onPressRedeem(rewardTier.id),
              type: 'outline',
              disabled: !isRedeemable,
            },
            children: (
              <CardContent
                title={rewardTier.name}
                description={rewardTier.message}
                isRedeemable={isRedeemable}
              />
            ),
          });
        }
        return cards;
      }, []) ?? []
    );
  }, [data?.rewardTiers, onPressRedeem, hasReachedMaxRedemptionLimit]);

  const renderHeader = useCallback(() => {
    return (
      <View style={styles.header}>
        <Text.Heading type="two">Redeem Rewards In-Restaurant</Text.Heading>
        <Text.Ui size="md">Choose what you want to redeem:</Text.Ui>
        {renderMaxRedemptionLimitAlert()}
      </View>
    );
  }, [renderMaxRedemptionLimitAlert]);

  const renderFooter = useCallback(() => {
    return <View style={{ height: bottom + 24 }} />;
  }, [bottom]);

  const renderEmpty = useCallback(() => {
    if (isLoading) {
      return (
        <View>
          <RewardTierSkeleton />
          <RewardTierSkeleton />
          <RewardTierSkeleton />
        </View>
      );
    }
    const firstRewardTier = data?.rewardTiers?.[0];
    if (firstRewardTier) {
      return (
        <DisplayCard
          image={firstRewardTier.image}
          button={{
            label: 'Redeem',
            onPress: () => onPressRedeem(firstRewardTier.id),
            type: 'outline',
            disabled: true,
          }}
        >
          <CardContent
            title={firstRewardTier.name}
            description={firstRewardTier.message}
            isRedeemable={false}
          />
        </DisplayCard>
      );
    }

    return null;
  }, [data?.rewardTiers, onPressRedeem, isLoading]);

  const handleSelectStore = useCallback(() => {
    router.dismissTo({
      pathname: '/store-locator/service-mode',
      params: { back: getCurrentPathname() + '?tab=redeem' },
    });
  }, [getCurrentPathname]);

  // Show unavailable state when redeem tab is disabled
  if (!isRedeemTabEnabled) {
    return <RedemptionUnavailable />;
  }

  if (noStoreSelected) {
    return (
      <ErrorCard
        buttonHandle={handleSelectStore}
        description="Please select a store to see available in-restaurant rewards."
        title="Select a Store to Redeem Rewards"
        buttonTitle="Select Store"
        buttonIcon={null}
        icon={<IconLocationFill size={80} color={tokens.colors.$blackOpacity10} />}
      />
    );
  }

  if (isError) {
    return <ErrorCard buttonHandle={refetch} />;
  }

  return (
    <ActionSheet.FlatList
      style={styles.tab}
      contentContainerStyle={styles.scrollContent}
      ListHeaderComponent={renderHeader}
      ListEmptyComponent={renderEmpty}
      ItemSeparatorComponent={renderSeparator}
      data={cardsToRender}
      renderItem={renderItem}
      ListFooterComponent={renderFooter}
    />
  );
}

const styles = StyleSheet.create({
  tab: {
    flex: 1,
    paddingTop: 24,
    paddingHorizontal: 16,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  header: {
    gap: 8,
    marginBottom: 16,
    alignItems: 'center',
  },
  separator: {
    marginBottom: 12,
  },
  skeletonCard: {
    marginBottom: 12,
  },
  imageSkeleton: {
    width: 40,
    height: 40,
    borderRadius: 100,
  },
  buttonSkeleton: {
    width: 86,
    height: 34,
  },
  skeletonText: {
    width: '70%',
  },
  unavailableContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    gap: 16,
  },
  unavailableContent: {
    alignItems: 'center',
    gap: 8,
  },
  unavailableTitle: {
    textAlign: 'center',
  },
  unavailableSubtitle: {
    textAlign: 'center',
    color: tokens.colors.$houseDark,
  },
});
